import {FormFragment, FormFragmentSchema} from 'apstra-ui-common';
import {filter, find, flatMap, groupBy, isMatch, mapValues, pullAllWith} from 'lodash';
import {observer} from 'mobx-react';
import pluralize from 'pluralize';
import {ComponentProps, FC, useCallback, useMemo} from 'react';
import {Message, Form, Icon} from 'semantic-ui-react';

import PredefinedDashboardsLinks from './PredefinedDashboardsLinks';
import {ProbeError} from './ProbeDetails/types';
import {WidgetComponentType} from '../types';

type WidgetFormProps = {
  errors?: ProbeError[];
  mode: 'create' | 'update';
  predefinedDashboards: {id: string; label: string;}[];
  properties: Record<string, any>;
  schema: FormFragmentSchema;
  setAggregationType: (value: string) => void;
  setProperty: (name: string, value: any) => void;
  setSpotlightMode: (value: string) => void;
  setValueColumnName: (value: string) => void;
  setWidgetType: (value: WidgetComponentType) => void;
  widgetType: string;
} & Omit<Partial<ComponentProps<typeof FormFragment>>, 'errors' | 'values' | 'onChange'>;

export const WidgetForm: FC<WidgetFormProps> = observer(({
  errors: errorsFromProps = [],
  mode,
  predefinedDashboards,
  properties,
  setAggregationType,
  setProperty,
  setSpotlightMode,
  setValueColumnName,
  setWidgetType,
  widgetType,
  ...props
}) => {
  const setPropertyValue = useCallback((name, value) => {
    if (name === 'type') {
      setWidgetType(value);
    } else if (name === 'spotlight_mode') {
      setSpotlightMode(value);
    } else if (name === 'value_column_name') {
      setValueColumnName(value);
    } else if (name === 'aggregation_type') {
      setAggregationType(value);
    } else {
      setProperty(name, value);
      pullAllWith(errorsFromProps, [{type: 'property', propertyName: name}], isMatch);
    }
  }, [errorsFromProps, setAggregationType, setProperty, setSpotlightMode, setValueColumnName, setWidgetType]);

  const errors = useMemo(() => ({
    form: mapValues(
      groupBy(filter(errorsFromProps, ({type}) => type === 'property'), ({propertyName}) => propertyName),
      (errorGroup) => flatMap(errorGroup, ({message}) => message)
    ),
    generic: find(errorsFromProps, ({type}) => type === 'generic'),
  }), [errorsFromProps]);

  return (
    <>
      <Form>
        <FormFragment
          {...props}
          values={{...properties, type: widgetType}}
          errors={errors.form}
          onChange={(name, value) => setPropertyValue(name, value)}
        />
      </Form>
      {mode === 'update' && predefinedDashboards.length > 0 && (
        <Message icon warning>
          <Icon name='warning sign' />
          <Message.Content>
            <Message.Header>{'Modification of Widget'}</Message.Header>
            <p>
              {'After saving the changes, the following predefined '}
              {pluralize('dashboard', predefinedDashboards.length)}
              {' will no longer be predefined:'}
              <PredefinedDashboardsLinks dashboards={predefinedDashboards} />
            </p>
          </Message.Content>
        </Message>
      )}
      {errors.generic &&
        <Message error icon='warning sign' header='Error' content={errors.generic.message} />
      }
    </>
  );
});
